import { classifyMeasurementEvidence } from "./evidencePresentation"

const formatKey = (date, range) => {
     if (range === "day") return date.toISOString().slice(0, 10)
     if (range === "month") return date.toISOString().slice(0, 7)
     return String(date.getUTCFullYear())
}

const startOfUtcDay = (date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))

const pointsFor = (range, now) => {
     const points = []
     if (range === "day") {
          const start = startOfUtcDay(now)
          for (let offset = 6; offset >= 0; offset -= 1) {
               const date = new Date(start)
               date.setUTCDate(date.getUTCDate() - offset)
               points.push(date)
          }
     } else if (range === "month") {
          for (let offset = 5; offset >= 0; offset -= 1) {
               points.push(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - offset, 1)))
          }
     } else {
          for (let offset = 2; offset >= 0; offset -= 1) {
               points.push(new Date(Date.UTC(now.getUTCFullYear() - offset, 0, 1)))
          }
     }
     return points
}

const labelFor = (date, range, locale) => {
     if (range === "day") return new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric" }).format(date)
     if (range === "month") return new Intl.DateTimeFormat(locale, { month: "short" }).format(date)
     return String(date.getUTCFullYear())
}

export const buildHistorySeries = (events = [], range = "day", referenceDate = new Date(), locale = "it-IT") => {
     const buckets = new Map(pointsFor(range, referenceDate).map((date) => [formatKey(date, range), {
          key: formatKey(date, range),
          label: labelFor(date, range, locale),
          events: 0,
          liters: 0,
          durationSeconds: 0,
          estimated: false,
     }]))

     events.forEach((event) => {
          const date = new Date(event.occurredAt)
          const bucket = buckets.get(formatKey(date, range))
          if (!bucket) return
          bucket.events += 1
          bucket.durationSeconds += Number(event.durationSeconds) || 0
          if (typeof event.volumeLiters === "number") bucket.liters += event.volumeLiters
          if (event.volumeQuality !== "measured") bucket.estimated = true
     })

     return [...buckets.values()]
}

export const summarizeSeries = (series) => series.reduce((summary, point) => ({
     events: summary.events + point.events,
     liters: summary.liters + point.liters,
     durationSeconds: summary.durationSeconds + point.durationSeconds,
     estimated: summary.estimated || point.estimated,
}), { events: 0, liters: 0, durationSeconds: 0, estimated: false })

export const labelIrrigationBuckets = (buckets = [], range = "day", locale = "it-IT") => buckets.map((bucket) => {
     const date = range === "day"
          ? new Date(`${bucket.key}T12:00:00Z`)
          : range === "month"
          ? new Date(`${bucket.key}-15T12:00:00Z`)
          : new Date(`${bucket.key}-07-01T12:00:00Z`)
     return { ...bucket, liters: Number(bucket.volumeLiters) || 0, label: labelFor(date, range, locale), estimated: false }
})

const SENSOR_RANGE_MILLISECONDS = {
     "24h": 24 * 60 * 60 * 1000,
     "7d": 7 * 24 * 60 * 60 * 1000,
     "30d": 30 * 24 * 60 * 60 * 1000,
}

const SENSOR_MEASUREMENTS = {
     soilMoisture: { label: "Umidità del suolo", unit: "%", color: "#397a58" },
     temperature: { label: "Temperatura", unit: "°C", color: "#c47a3d" },
     ec: { label: "EC del terreno (bulk)", unit: "µS/cm", color: "#477b9f" },
     poreWaterEc: { label: "EC acqua interstiziale", unit: "µS/cm", color: "#2d8790" },
     ph: { label: "pH", unit: "pH", color: "#8b6cae" },
     waterEc: { label: "EC soluzione", unit: "µS/cm", color: "#477b9f" },
     nitrogen: { label: "Azoto", unit: "mg/kg", color: "#668b4a" },
     phosphorus: { label: "Fosforo", unit: "mg/kg", color: "#9b7653" },
     potassium: { label: "Potassio", unit: "mg/kg", color: "#b18436" },
}

export const sensorRangeWindow = (range = "24h", referenceDate = new Date()) => {
     const end = new Date(referenceDate)
     const duration = SENSOR_RANGE_MILLISECONDS[range] || SENSOR_RANGE_MILLISECONDS["24h"]
     return { start: new Date(end.getTime() - duration), end }
}

export const filterSensorReadings = (readings = [], range = "24h", referenceDate = new Date()) => {
     const { start, end } = sensorRangeWindow(range, referenceDate)
     return readings
          .filter((reading) => {
               const sampledAt = new Date(reading.sampledAt)
               return Number.isFinite(sampledAt.getTime()) && sampledAt >= start && sampledAt <= end
          })
          .sort((left, right) => new Date(left.sampledAt) - new Date(right.sampledAt))
}

const validIsoTimestamp = (value) => {
     if (typeof value !== "string" && !(value instanceof Date)) return null
     if (typeof value === "string" && !value.trim()) return null
     const parsed = new Date(value)
     return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : null
}

const validWindowTimestamp = (value, fallback) => validIsoTimestamp(value) || validIsoTimestamp(fallback)

const bucketHistoryAsSeries = (payload) => {
     if (!payload || !Array.isArray(payload.buckets)) return payload
     const seriesBySignature = new Map()
     const variantsByKey = new Map()
     payload.buckets.forEach((bucket) => {
          const sampledAt = validIsoTimestamp(bucket?.bucketStart)
          if (!sampledAt) return
          ;(Array.isArray(bucket?.measurements) ? bucket.measurements : []).forEach((measurement) => {
               const baseKey = String(measurement?.key || "").trim()
               if (!baseKey) return
               const metadataVariantsForKey = measurement.metadataMixedForKey === true
               const method = measurement.method
               const evidenceKind = measurement.evidenceKind
               const calibrationStatus = measurement.calibrationStatus
               const signature = [baseKey, measurement.measurementId, measurement.unit, method, evidenceKind, calibrationStatus].join("|")
               if (!seriesBySignature.has(signature)) {
                    const variant = variantsByKey.get(baseKey) || 0
                    variantsByKey.set(baseKey, variant + 1)
                    seriesBySignature.set(signature, {
                         key: variant ? `${baseKey}__evidence_${variant + 1}` : baseKey,
                         label: variant ? `${measurement.label || baseKey} · ${method || evidenceKind || variant + 1}` : measurement.label || baseKey,
                         measurementId: measurement.measurementId,
                         unit: measurement.unit || "",
                         method,
                         evidenceKind,
                         calibrationStatus,
                         points: [],
                    })
               }
               const normalized = measurement.normalized || {}
               const count = Number.isFinite(normalized.count) ? normalized.count : measurement.sampleCount
               seriesBySignature.get(signature).points.push({
                    sampledAt,
                    value: Number.isFinite(normalized.mean) ? normalized.mean : null,
                    minimum: Number.isFinite(normalized.min) ? normalized.min : null,
                    maximum: Number.isFinite(normalized.max) ? normalized.max : null,
                    sampleCount: Number.isFinite(count) ? count : null,
                    qualityFlags: [measurement.quality, metadataVariantsForKey ? "metadata_variants_for_key" : null].filter(Boolean),
               })
          })
     })
     return {
          ...payload,
          from: payload.fromInclusive,
          to: payload.toExclusive,
          aggregation: {
               method: payload.aggregation?.statistic || "mean_min_max_count",
               bucketSeconds: payload.bucketSeconds,
               fillsMissingBuckets: false,
          },
          series: [...seriesBySignature.values()],
     }
}

export const normalizeSensorHistoryResponse = (payload, requested = {}) => {
     if (Array.isArray(payload)) {
          return {
               mode: "legacy",
               range: requested.range || "24h",
               from: validWindowTimestamp(requested.from, null),
               to: validWindowTimestamp(requested.to, null),
               readOnly: true,
               controlsHardware: false,
               aggregation: null,
               readings: payload,
          }
     }
     payload = bucketHistoryAsSeries(payload)
     if (!payload || !Array.isArray(payload.series)) throw new TypeError("Invalid sensor history response")
     if (payload.readOnly !== true || payload.controlsHardware !== false) {
          throw new TypeError("Sensor history response violates the read-only boundary")
     }

     const bucketSeconds = Number(payload.aggregation?.bucketSeconds)
     const bucketMilliseconds = Number.isFinite(bucketSeconds) && bucketSeconds > 0 ? bucketSeconds * 1000 : null
     const normalizedSeries = payload.series.map((series, seriesIndex) => ({
          ...series,
          key: String(series?.key || "").trim(),
          points: (Array.isArray(series?.points) ? series.points : [])
               .map((point, pointIndex) => ({
                    ...point,
                    sampledAt: validIsoTimestamp(point?.sampledAt),
                    sourceIndex: pointIndex,
               }))
               .filter((point) => point.sampledAt)
               .sort((left, right) => new Date(left.sampledAt) - new Date(right.sampledAt)),
          sourceIndex: seriesIndex,
     })).filter((series) => series.key)

     const timestamps = [...new Set(normalizedSeries.flatMap((series) => series.points.map((point) => point.sampledAt)))].sort()
     const timestampIndex = new Map(timestamps.map((sampledAt, index) => [sampledAt, index]))
     const rows = new Map(timestamps.map((sampledAt) => [sampledAt, {
          id: `history-bucket:${sampledAt}`,
          sampledAt,
          source: "server_aggregate",
          aggregate: true,
          timeBasis: payload.timeBasis || "UTC",
          bucketSeconds: bucketMilliseconds ? bucketSeconds : null,
          measurements: [],
     }]))

     normalizedSeries.forEach((series) => {
          let previous = null
          series.points.forEach((point) => {
               let missingBefore = 0
               if (bucketMilliseconds && previous) {
                    const elapsedBuckets = Math.max(1, Math.round((new Date(point.sampledAt) - new Date(previous.sampledAt)) / bucketMilliseconds))
                    const unionRowsBetween = Math.max(0, timestampIndex.get(point.sampledAt) - timestampIndex.get(previous.sampledAt) - 1)
                    missingBefore = Math.max(0, elapsedBuckets - 1 - unionRowsBetween)
               }
               rows.get(point.sampledAt).measurements.push({
                    key: series.key,
                    label: series.label || series.key,
                    measurementId: series.measurementId,
                    unit: series.unit || "",
                    normalizedValue: Number.isFinite(point.value) ? point.value : null,
                    minimum: Number.isFinite(point.minimum) ? point.minimum : null,
                    maximum: Number.isFinite(point.maximum) ? point.maximum : null,
                    sampleCount: Number.isFinite(point.sampleCount) ? point.sampleCount : null,
                    qualityFlags: Array.isArray(point.qualityFlags) ? point.qualityFlags : [],
                    missingBefore,
                    method: series.method,
                    evidenceKind: series.evidenceKind,
                    calibrationStatus: series.calibrationStatus,
               })
               previous = point
          })
     })

     return {
          mode: "aggregate",
          range: payload.range || requested.range || "24h",
          from: validWindowTimestamp(payload.from, requested.from),
          to: validWindowTimestamp(payload.to, requested.to),
          timeBasis: payload.timeBasis || "UTC",
          readOnly: true,
          controlsHardware: false,
          aggregation: {
               method: payload.aggregation?.method || "unknown",
               bucketSeconds: bucketMilliseconds ? bucketSeconds : null,
               fillsMissingBuckets: payload.aggregation?.fillsMissingBuckets === true,
          },
          readings: [...rows.values()],
     }
}

export const sensorMetricDescriptors = (sensor, readings = [], translateLabel = null) => {
     const observed = new Map()
     readings.forEach((reading) => reading.measurements?.forEach((measurement) => {
          if (!observed.has(measurement.key)) observed.set(measurement.key, [])
          observed.get(measurement.key).push(measurement)
     }))
     const configured = Object.keys(sensor?.measurementMappings || {})
     const keys = [...new Set([...configured, ...observed.keys()])]
     const priority = ["soilMoisture", "temperature", "ec", "poreWaterEc", "ph", "waterEc"]
     keys.sort((left, right) => {
          const leftPriority = priority.indexOf(left)
          const rightPriority = priority.indexOf(right)
          return (leftPriority < 0 ? 99 : leftPriority) - (rightPriority < 0 ? 99 : rightPriority)
     })
     return keys.map((key, index) => {
          const samples = observed.get(key) || []
          const sample = samples.find(Boolean)
          const fallback = SENSOR_MEASUREMENTS[key] || {}
          return {
               key,
               label: translateLabel ? translateLabel(key, fallback.label || sample?.label || key) : fallback.label || sample?.label || key,
               unit: sample?.unit || fallback.unit || "",
               color: fallback.color || ["#397a58", "#c47a3d", "#477b9f", "#8b6cae"][index % 4],
               evidence: classifyMeasurementEvidence(samples),
          }
     })
}

export const buildSensorTrend = (readings = [], measurementKey) => readings.map((reading) => {
     const measurement = reading.measurements?.find((candidate) => candidate.key === measurementKey)
     const normalizedValue = measurement?.normalizedValue
     const aggregateFields = measurement && Object.prototype.hasOwnProperty.call(measurement, "minimum") ? {
          minimum: Number.isFinite(measurement.minimum) ? measurement.minimum : null,
          maximum: Number.isFinite(measurement.maximum) ? measurement.maximum : null,
          sampleCount: Number.isFinite(measurement.sampleCount) ? measurement.sampleCount : null,
          qualityFlags: measurement.qualityFlags,
          missingBefore: Number.isFinite(measurement.missingBefore) ? measurement.missingBefore : 0,
     } : {}
     return {
          readingId: reading.id,
          sampledAt: reading.sampledAt,
          value: Number.isFinite(normalizedValue) ? normalizedValue : null,
          ...aggregateFields,
          method: measurement?.method,
          evidenceKind: measurement?.evidenceKind,
          calibrationStatus: measurement?.calibrationStatus,
     }
})

export const summarizeSensorTrend = (points = []) => {
     const actual = points.filter((point) => Number.isFinite(point.value))
     const minimumValues = actual.map((point) => Number.isFinite(point.minimum) ? point.minimum : point.value)
     const maximumValues = actual.map((point) => Number.isFinite(point.maximum) ? point.maximum : point.value)
     return {
          actualCount: actual.length,
          sampleCount: actual.reduce((sum, point) => sum + (Number.isFinite(point.sampleCount) ? point.sampleCount : 1), 0),
          missingCount: points.length - actual.length + actual.reduce((sum, point) => sum + (point.missingBefore || 0), 0),
          latest: actual.length ? actual[actual.length - 1].value : null,
          minimum: minimumValues.length ? Math.min(...minimumValues) : null,
          maximum: maximumValues.length ? Math.max(...maximumValues) : null,
     }
}

export const sampleTrendSegment = (values = [], maximumPoints = 280) => {
     if (maximumPoints < 2) throw new Error("maximumPoints must be at least 2")
     if (values.length <= maximumPoints) return values
     return Array.from(
          { length: maximumPoints },
          (_, index) => values[Math.round(index * (values.length - 1) / (maximumPoints - 1))],
     )
}
